import { Component, signal } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import readXlsxFile from 'read-excel-file/browser';
import writeXlsxFile from 'write-excel-file/browser';
import { MassiveServices2Service } from '../services/massive-services2.service';

@Component({
  selector: 'app-massive-services2-upload',
  imports: [CommonModule, FormsModule],
  templateUrl: './massive-services2-upload.html',
  styleUrl: './massive-services2-upload.css',
})
export class MassiveServices2Upload {
  excelFilePath = signal<string>('');
  errorMessage = signal<string>('');
  successMessage = signal<string>('');
  isSending = signal<boolean>(false);
  isValidExcel = signal<boolean>(false);

  portfolioId = '';
  processManagerId = '';

  readonly REQUIRED_HEADERS = [
    'SOLICITUD', 'REFERENCIA_CATASTRAL', 'REF_EXTERNA', 'IMPORTE',
    'HONORARIOS_TECNICOS', 'NOTA_SIMPLE', 'ENTIDAD', 'BOLSA'
  ];

  private selectedExcelFile: File | null = null;

  constructor(private router: Router, private massiveServices2Service: MassiveServices2Service) {}

  goBack() {
    this.router.navigate(['/home']);
  }

  selectExcelFile() {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', '.xlsx,.xls');

    input.onchange = async (event: Event) => {
      const target = event.target as HTMLInputElement;
      const file = target.files?.[0];
      if (file) {
        this.selectedExcelFile = file;
        this.excelFilePath.set(file.name);
        this.errorMessage.set('');
        this.successMessage.set('');
        this.isValidExcel.set(false);
        await this.validateExcelHeaders(file);
      }
    };

    input.click();
  }

  async validateExcelHeaders(file: File): Promise<void> {
    try {
      const rows = await readXlsxFile(file);
      if (rows.length === 0) {
        this.errorMessage.set('El Excel no tiene el formato correcto');
        return;
      }
      const headers = rows[0].map(cell => (cell !== null && cell !== undefined ? String(cell) : ''));
      const headersMatch =
        headers.length === this.REQUIRED_HEADERS.length &&
        headers.every((h, i) => h === this.REQUIRED_HEADERS[i]);
      if (!headersMatch) {
        this.errorMessage.set('El Excel no tiene el formato correcto. Las columnas deben ser: ' + this.REQUIRED_HEADERS.join(', '));
      } else {
        this.isValidExcel.set(true);
      }
    } catch {
      this.errorMessage.set('El Excel no tiene el formato correcto');
    }
  }

  canSend(): boolean {
    return !!this.selectedExcelFile && this.isValidExcel() && !this.isSending();
  }

  async downloadExampleExcel(): Promise<void> {
    const EXAMPLE_ROW_1 = [
      '194326', '23164010H1221N0029MZ', '202600391180', '81',
      '45', '10,46', 'Banco Santander, S.A.', 'SI'
    ];
    const EXAMPLE_ROW_2 = [
      '194325', '89359220767588H0529', '202600391180', '50',
      '50', '12,50', 'BBVA', ''
    ];
    const data = [this.REQUIRED_HEADERS, EXAMPLE_ROW_1, EXAMPLE_ROW_2];
    await writeXlsxFile(data, { fileName: 'ejemplo_servicios_masivos_1-1.xlsx' });
  }

  async sendRequest() {
    if (!this.canSend()) {
      return;
    }

    this.isSending.set(true);
    this.errorMessage.set('');
    this.successMessage.set('');

    try {
      await firstValueFrom(
        this.massiveServices2Service.createByMassiveExcel2({
          excelFile: this.selectedExcelFile!,
          portfolioId: this.portfolioId.trim() || undefined,
          processManagerId: this.processManagerId.trim() || undefined,
        })
      );
      this.successMessage.set('Solicitud de altas masivas relación 1-1 enviada correctamente');
    } catch (error: any) {
      const message = error.error?.message || error.message || 'Error desconocido al enviar la solicitud';
      this.errorMessage.set(`Error en el envío: ${message}`);
    } finally {
      this.isSending.set(false);
    }
  }
}
